import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { ConfirmDialog } from '@/components/patterns/ConfirmDialog';
import { toast } from '@/lib/notify';
import { useUiStore } from '@/stores/uiStore';

import { useDeleteSession } from '../api/useDeleteSession';
import { useSession } from '../api/useSession';
import { useSessionQuestionnaireNames } from '../api/useSessionQuestionnaireNames';
import { OverviewAccordion } from '../components/detail/OverviewAccordion';
import { SessionDetailHeader } from '../components/detail/SessionDetailHeader';
import { SessionListPanel } from '../components/detail/SessionListPanel';
import { type OpenTab, SessionTabsBar } from '../components/detail/SessionTabsBar';
import { StepAccordion } from '../components/detail/StepAccordion';
import { displayApplicantName } from '../sessionReference';

const TABS_KEY = 'sc.session.openTabs';

function loadTabs(): OpenTab[] {
  try {
    const raw = sessionStorage.getItem(TABS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Session detail (view) — the designer's layout wired to real data:
 *   left  : full session list (search + filter), click to open
 *   right : open-session tabs, then the step-chip header, then ONE scrolling column of
 *           accordions — Overview (warnings + session details) followed by one accordion
 *           per verification step (its details beside its checks, plus the risk rules
 *           that governed it).
 *
 * The accordion column replaced a three-pane body (step nav | step detail | checks list).
 * That layout showed one step at a time, so answering "what happened in this run" meant
 * clicking through every step; here it is a scroll, and a step's data is no longer split
 * across two columns.
 */
export function SessionViewScreen() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const setSidebarCollapsed = useUiStore((s) => s.setSidebarCollapsed);
  const { data: session, isLoading } = useSession(sessionId);
  const deleteSession = useDeleteSession();

  const [openTabs, setOpenTabs] = useState<OpenTab[]>(loadTabs);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // One DOM node per step accordion, so a header chip can scroll to its step. A Map in a
  // ref rather than state: these are written during render commit and only read inside an
  // event handler, so storing them in state would re-render the page for no visual change.
  const stepRefs = useRef(new Map<string, HTMLDivElement>());

  const registerStepRef = useCallback(
    (stepId: string) => (el: HTMLDivElement | null) => {
      if (el) stepRefs.current.set(stepId, el);
      else stepRefs.current.delete(stepId);
    },
    [],
  );

  // Which step accordions are expanded. Owned here rather than inside each accordion so a
  // header chip can OPEN its step, not merely scroll to a collapsed card.
  const [openSteps, setOpenSteps] = useState<Record<string, boolean>>({});

  const setStepOpen = useCallback((stepId: string, open: boolean) => {
    setOpenSteps((prev) => ({ ...prev, [stepId]: open }));
  }, []);

  // The step whose card is currently at the top of the scrolled body — drives the header
  // chip highlight. Tracked by observation rather than set only on click, so scrolling by
  // hand moves the highlight too; a chip row that only updated on click would go stale the
  // moment the admin used the wheel.
  const [activeStepId, setActiveStepId] = useState<string | undefined>(undefined);

  // The scrolling body. Needed as the IntersectionObserver root: the page itself does not
  // scroll on this screen, this element does, so observing against the viewport would report
  // every card as visible.
  const scrollRef = useRef<HTMLDivElement | null>(null);

  /**
   * The step a chip click is scrolling TO, while that scroll is still running.
   *
   * This is what stops the highlight bouncing. `scrollIntoView({ behavior: 'smooth' })`
   * animates over several hundred milliseconds, and the observer keeps firing throughout —
   * so clicking Aadhaar set the chip to Aadhaar, then the very next callback reported PAN
   * (still the top card, the scroll had barely moved) and stole it back, and only when the
   * animation finally landed did it return to Aadhaar. Visibly: PAN → flick → Aadhaar.
   *
   * While this holds a step id, the observer accepts ONLY that step — every other report is
   * a frame of the journey, not a destination.
   */
  const scrollingTo = useRef<string | null>(null);
  const scrollingToTimer = useRef<number | undefined>(undefined);

  /** Expand a step and bring it into view — what a header chip does. */
  const focusStep = useCallback((stepId: string) => {
    setOpenSteps((prev) => ({ ...prev, [stepId]: true }));
    // Highlight immediately. The observer will confirm it a frame later, but waiting for
    // the smooth scroll to finish before the chip responds feels like a dropped click.
    setActiveStepId(stepId);

    scrollingTo.current = stepId;
    // A safety release, and it is NOT optional. The target may never reach the top band at
    // all — the last step in a run cannot scroll above the fold — in which case the "we
    // arrived" check below never fires and the observer would stay muted for the rest of the
    // session. 1200ms comfortably outlasts a smooth scroll.
    window.clearTimeout(scrollingToTimer.current);
    scrollingToTimer.current = window.setTimeout(() => {
      scrollingTo.current = null;
    }, 1200);
    // Scroll on the next frame, after the expansion has been committed. Scrolling in the
    // same tick would measure the still-collapsed card; `block: 'start'` also means the
    // card's own growth cannot move the target out from under us.
    requestAnimationFrame(() => {
      stepRefs.current.get(stepId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  // Release the safety timer on unmount, so a pending release cannot fire after the screen
  // has gone.
  useEffect(() => () => window.clearTimeout(scrollingToTimer.current), []);

  // Collapse the app sidebar for this dense screen (restore on leave).
  useEffect(() => {
    const prev = useUiStore.getState().sidebarCollapsed;
    setSidebarCollapsed(true);
    return () => setSidebarCollapsed(prev);
  }, [setSidebarCollapsed]);

  // Persist open tabs across navigation within the session area.
  useEffect(() => {
    sessionStorage.setItem(TABS_KEY, JSON.stringify(openTabs));
  }, [openTabs]);

  // Ensure the active (URL) session is present as a tab once its name is known.
  useEffect(() => {
    if (!session) return;
    setOpenTabs((tabs) =>
      tabs.some((t) => t.id === session.id)
        ? tabs
        : [...tabs, { id: session.id, name: displayApplicantName(session.applicantFullName) }],
    );
  }, [session]);

  const openSession = useCallback(
    (id: string, name: string) => {
      setOpenTabs((tabs) => (tabs.some((t) => t.id === id) ? tabs : [...tabs, { id, name }]));
      if (id !== sessionId) navigate(`/sessions/${id}`);
    },
    [navigate, sessionId],
  );

  const closeTab = useCallback(
    (id: string) => {
      setOpenTabs((tabs) => {
        const next = tabs.filter((t) => t.id !== id);
        // If we closed the active session, move to a neighbour (or the list).
        if (id === sessionId) {
          const fallback = next[next.length - 1];
          navigate(fallback ? `/sessions/${fallback.id}` : '/sessions');
        }
        return next;
      });
    },
    [navigate, sessionId],
  );

  const steps = useMemo(
    () => (session ? session.steps.slice().sort((a, b) => a.position - b.position) : []),
    [session],
  );

  /**
   * Questionnaire names, so a questionnaire step reads as the questions it asked rather than as
   * "Questionnaire" — in the header chips AND in each step's accordion title.
   *
   * Resolved from the questionnaire because the step's own `displayName` is frozen at run start: a step
   * created before the builder named these carries the generic label, and the frozen snapshot that
   * holds the real name is not exposed to the client. Shares its cache with the answers view, so this
   * costs nothing once a step is opened.
   */
  const questionnaireNames = useSessionQuestionnaireNames(sessionId, steps);

  // Open the first step once a session's data arrives, and reset when switching sessions.
  //
  // The ref is what makes this run ONCE per session while keeping honest dependencies.
  // Depending on `steps` alone would re-run on every background refetch and re-collapse
  // cards the admin had opened mid-read; narrowing the dependency list to silence that
  // would only hide the problem from the linter.
  const initialisedFor = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!session || initialisedFor.current === session.id) return;
    initialisedFor.current = session.id;
    const first = steps[0];
    setOpenSteps(first ? { [first.id]: true } : {});
    setActiveStepId(first?.id);
  }, [session, steps]);

  // Which step is "current" as the body scrolls.
  //
  // `rootMargin: '0px 0px -65% 0px'` shrinks the observation band to the TOP THIRD of the
  // scroller. Without it every card on a tall screen intersects at once and the highlight
  // would land on whichever happened to be reported last — the chip would flicker between
  // steps while scrolling. Narrowing it to the top makes "current" mean "the card you are
  // reading", which is what the eye expects.
  //
  // Rebuilt when the step list changes, and torn down on unmount. Purely additive: if
  // IntersectionObserver is unavailable the chips simply do not highlight, and the click-to-
  // scroll they already had is untouched.
  useEffect(() => {
    const root = scrollRef.current;
    if (!root || steps.length === 0 || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        // The topmost card still inside the band wins. Reading `boundingClientRect` rather
        // than trusting entry order matters — the callback receives only what CHANGED, in no
        // guaranteed order, so picking the first entry would sometimes choose a card below.
        let best: { id: string; top: number } | null = null;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const id = (entry.target as HTMLElement).dataset.stepId;
          if (!id) continue;
          const top = entry.boundingClientRect.top;
          if (!best || top < best.top) best = { id, top };
        }
        if (!best) return;

        // Mid-flight from a chip click: ignore everything except arriving at the target.
        // See `scrollingTo` — this is the whole fix for the highlight bouncing back to the
        // previous step for a moment.
        const target = scrollingTo.current;
        if (target !== null) {
          if (best.id !== target) return;
          scrollingTo.current = null;
          window.clearTimeout(scrollingToTimer.current);
        }

        setActiveStepId(best.id);
      },
      { root, rootMargin: '0px 0px -65% 0px', threshold: 0 },
    );

    for (const el of stepRefs.current.values()) observer.observe(el);
    return () => observer.disconnect();
  }, [steps]);

  function handleDelete() {
    if (!sessionId) return;
    deleteSession.mutate(sessionId, {
      onSuccess: () => {
        toast.success('Session deleted');
        setConfirmDelete(false);
        closeTab(sessionId);
      },
      onError: () => {
        toast.error('Failed to delete session');
        setConfirmDelete(false);
      },
    });
  }

  return (
    <AppShell title="Sessions" backTo="/sessions" noPadding>
      <div className="flex h-full w-full gap-[clamp(12px,1vw,16px)] overflow-hidden bg-[#F4F6FD] p-[clamp(12px,1vw,16px)]">
        {/* Left — session list */}
        <div className="hidden w-[clamp(260px,24vw,330px)] shrink-0 lg:block">
          <SessionListPanel
            selectedId={sessionId}
            onSelect={(s) => openSession(s.id, displayApplicantName(s.applicantFullName))}
          />
        </div>

        {/* Right — tabs + header + (nav | detail | checks) */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-2xl bg-white">
          <SessionTabsBar
            tabs={openTabs}
            activeId={sessionId}
            onSelect={(id) => id !== sessionId && navigate(`/sessions/${id}`)}
            onClose={closeTab}
          />

          {session ? (
            <>
              <SessionDetailHeader
                session={session}
                stepNames={questionnaireNames}
                onDownloadPdf={() => toast.info('PDF export is coming soon.')}
                onDelete={() => setConfirmDelete(true)}
                onFocusStep={focusStep}
                activeStepId={activeStepId}
              />

              {/* The design's single scrolling column of accordions, which replaced a
                  three-pane layout (step nav | detail | checks). The panes forced an
                  admin to click through steps one at a time to answer "what happened in
                  this run"; stacked accordions answer it by scrolling, and the step's
                  details and its checks sit side by side instead of in separate columns. */}
              <div
                ref={scrollRef}
                className="flex min-h-0 flex-1 flex-col gap-[clamp(12px,1.1vw,18px)] overflow-y-auto bg-white px-[clamp(12px,1.1vw,18px)] pb-[clamp(12px,1.1vw,18px)]"
              >
                <OverviewAccordion session={session} />

                {steps.map((step) => (
                  // `data-step-id` is how the IntersectionObserver callback knows WHICH step
                  // an observed node belongs to — the entry only carries the element.
                  <div key={step.id} ref={registerStepRef(step.id)} data-step-id={step.id}>
                    {/* Only the first step starts expanded (see the effect above).
                        Expanding all of them on a long workflow buries the Overview and
                        opens the page mid-scroll; a header chip expands any other. */}
                    <StepAccordion
                      step={step}
                      sessionId={sessionId}
                      titleOverride={questionnaireNames.get(step.id)}
                      open={openSteps[step.id] ?? false}
                      onOpenChange={(open) => setStepOpen(step.id, open)}
                    />
                  </div>
                ))}

                {steps.length === 0 && (
                  <p className="p-4 text-center text-[length:var(--text-14)] text-[#666666]">
                    This session has no steps.
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center bg-[#FCFCFD] text-[length:var(--text-14)] text-[#666666]">
              {isLoading ? 'Loading session…' : 'Select a session from the list.'}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title={`Delete session for "${displayApplicantName(session?.applicantFullName)}"?`}
        description="This permanently deletes the session and all its step data. This cannot be undone."
        confirmLabel="Delete"
        destructive
        isPending={deleteSession.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </AppShell>
  );
}
